// URL de l'API - toujours relative pour cohérence
const API_URL = import.meta.env.VITE_API_URL || '/cv/api';

/**
 * Helper pour construire l'URL API correctement
 */
function apiUrl(path) {
  return API_URL.endsWith('/api') ? `${API_URL}${path}` : `${API_URL}/api${path}`;
}

/**
 * Helper pour les requêtes admin authentifiées (Bearer JWT session)
 */
async function adminFetch(path, options = {}) {
  const adminToken = sessionStorage.getItem('adminSessionToken');
  if (!adminToken) {
    throw new Error('Session expirée. Veuillez vous reconnecter.');
  }

  const response = await fetch(apiUrl(path), {
    ...options,
    headers: {
      ...options.headers,
      'Authorization': `Bearer ${adminToken}`,
    },
  });

  if (response.status === 401) {
    sessionStorage.removeItem('adminSessionToken');
    window.location.href = '/cv/admin/login';
    throw new Error('Session expirée');
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || errorData.error || `Erreur ${response.status}`);
  }

  return response.json();
}

// ============================================
// AUTH ADMIN (V2 — server-side)
// ============================================

/**
 * Login admin via endpoint serveur (credentials jamais exposés côté client)
 * @param {string} username
 * @param {string} password
 * @returns {Promise<Object>} { success, token, username }
 */
export async function adminLogin(username, password) {
  const response = await fetch(apiUrl('/admin/login'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username, password }),
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || 'Identifiants incorrects');
  }

  // Stocker le JWT session
  sessionStorage.setItem('adminSessionToken', data.token);
  return data;
}

/**
 * Vérifie si la session admin est encore valide
 * @returns {Promise<boolean>}
 */
export async function verifyAdminSession() {
  const token = sessionStorage.getItem('adminSessionToken');
  if (!token) return false;

  try {
    const response = await fetch(apiUrl('/admin/verify'), {
      headers: { 'Authorization': `Bearer ${token}` },
    });
    const data = await response.json();
    return data.valid === true;
  } catch {
    return false;
  }
}

/**
 * Déconnexion admin
 */
export function adminLogout() {
  sessionStorage.removeItem('adminSessionToken');
}

// ============================================
// PDF VIEWER (JWT recruteur)
// ============================================

/**
 * Récupère le PDF ou l'ensemble de documents depuis l'API avec authentification JWT
 * @param {string} token - Token JWT
 * @returns {Promise<Object>} Configuration PDF (V1) ou métadonnées ensemble (V2)
 */
export async function fetchPDF(token) {
  try {
    const url = API_URL.endsWith('/api') 
      ? `${API_URL}/pdf/view` 
      : `${API_URL}/api/pdf/view`;
    
    console.log('🔍 Fetching from:', url);
    console.log('🔑 Token:', token.substring(0, 20) + '...');
    
    // Faire une requête GET pour vérifier le mode (V1 ou V2)
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || errorData.error || `Erreur ${response.status}`);
    }
    
    // Vérifier le Content-Type pour détecter le mode
    const contentType = response.headers.get('Content-Type');
    
    if (contentType && contentType.includes('application/json')) {
      // Mode V2 : ensemble de documents (JSON)
      const data = await response.json();
      console.log('✅ Mode V2 détecté:', data);
      return {
        mode: 'v2',
        token,
        ...data
      };
    } else {
      // Mode V1 : fichier PDF unique
      console.log('✅ Mode V1 détecté: PDF binaire');
      return {
        mode: 'v1',
        url: url,
        httpHeaders: {
          'Authorization': `Bearer ${token}`,
        },
      };
    }
  } catch (error) {
    console.error('❌ Erreur lors de la récupération:', error);
    throw error;
  }
}

/**
 * Génère un token JWT pour un recruteur
 * @param {Object} data - Données du recruteur
 * @returns {Promise<Object>} Réponse avec token
 */
export async function generateToken(data) {
  return adminFetch('/auth/generate-access', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}

/**
 * Enregistre un téléchargement de PDF
 * @param {string} token - Token JWT
 * @returns {Promise<Object>} Réponse de l'API
 */
export async function trackDownload(token) {
  try {
    const url = API_URL.endsWith('/api') 
      ? `${API_URL}/pdf/track-download` 
      : `${API_URL}/api/pdf/track-download`;
    
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      console.error('Erreur tracking download:', response.status);
    }

    return await response.json();
  } catch (error) {
    console.error('Erreur lors du tracking du téléchargement:', error);
    // Ne pas bloquer le téléchargement si le tracking échoue
  }
}

/**
 * Récupère tous les tokens depuis la base de données
 * @returns {Promise<Array>} Liste des tokens
 */
export async function fetchAllTokens() {
  const data = await adminFetch('/admin/tokens');
  return data.tokens || [];
}

/**
 * Révoque un token (le rend inutilisable)
 * @param {number} tokenId - ID du token à révoquer
 * @returns {Promise<Object>} Réponse de l'API
 */
export async function revokeToken(tokenId) {
  return adminFetch(`/admin/tokens/${tokenId}/revoke`, { method: 'POST' });
}

/**
 * Supprime définitivement un token
 * @param {number} tokenId - ID du token à supprimer
 * @returns {Promise<Object>} Réponse de l'API
 */
export async function deleteToken(tokenId) {
  return adminFetch(`/admin/tokens/${tokenId}`, { method: 'DELETE' });
}

/**
 * Met à jour les propriétés d'un token
 * @param {number} tokenId - ID du token à modifier
 * @param {Object} data - Données à mettre à jour
 * @returns {Promise<Object>} Réponse de l'API
 */
export async function updateToken(tokenId, data) {
  return adminFetch(`/admin/tokens/${tokenId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}

/**
 * Prolonge un token de X heures
 * @param {number} tokenId - ID du token à prolonger
 * @param {number} hours - Nombre d'heures à ajouter
 * @returns {Promise<Object>} Réponse de l'API
 */
export async function extendToken(tokenId, hours = 48) {
  return adminFetch(`/admin/tokens/${tokenId}/extend`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ hours }),
  });
}

// ============================================
// DOCUMENT SETS & DOCUMENTS (V2)
// ============================================

/**
 * Récupère tous les ensembles de documents
 * @returns {Promise<Array>} Liste des ensembles
 */
export async function fetchDocumentSets() {
  const data = await adminFetch('/document-sets');
  return data.sets || [];
}

/**
 * Récupère un ensemble avec ses documents
 * @param {number} setId - ID de l'ensemble
 * @returns {Promise<Object>} Ensemble avec ses documents
 */
export async function fetchDocumentSet(setId) {
  const data = await adminFetch(`/document-sets/${setId}`);
  return data.set;
}

/**
 * Crée un nouvel ensemble de documents
 * @param {Object} data - { label, description }
 * @returns {Promise<Object>} Ensemble créé
 */
export async function createDocumentSet(data) {
  return adminFetch('/document-sets', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}

/**
 * Met à jour un ensemble de documents
 * @param {number} setId - ID de l'ensemble
 * @param {Object} data - { label, description }
 * @returns {Promise<Object>} Ensemble mis à jour
 */
export async function updateDocumentSet(setId, data) {
  return adminFetch(`/document-sets/${setId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}

/**
 * Supprime un ensemble de documents
 * @param {number} setId - ID de l'ensemble
 * @returns {Promise<Object>}
 */
export async function deleteDocumentSet(setId) {
  return adminFetch(`/document-sets/${setId}`, { method: 'DELETE' });
}

/**
 * Ajoute un document à un ensemble
 * @param {Object} data - { set_id, category, category_label, file_name, download_filename, sort_order }
 * @returns {Promise<Object>} Document créé
 */
export async function addDocument(data) {
  return adminFetch('/documents', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}

/**
 * Supprime un document
 * @param {number} docId - ID du document
 * @returns {Promise<Object>}
 */
export async function deleteDocument(docId) {
  return adminFetch(`/documents/${docId}`, { method: 'DELETE' });
}

/**
 * Met à jour un document
 * @param {number} docId - ID du document
 * @param {Object} data - Données à mettre à jour
 * @returns {Promise<Object>}
 */
export async function updateDocument(docId, data) {
  return adminFetch(`/documents/${docId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}

export async function fetchSiteSettings() {
  const data = await adminFetch('/site-settings/admin/all');
  return data.settings.reduce((acc, { setting_key, setting_value }) => {
    acc[setting_key] = setting_value ?? '';
    return acc;
  }, {});
}

export async function updateSiteSetting(key, value) {
  return adminFetch(`/site-settings/${key}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ value }),
  });
}
